import classNames from "components/ui/classNames";
import { ChevronDownIcon, XIcon } from "lucide-react";
import type {
  DropdownIndicatorProps,
  Props,
  ClearIndicatorProps,
  MultiValueRemoveProps,
  GroupBase,
} from "react-select";
import Select, { components } from "react-select";

function DropdownIndicator(props: DropdownIndicatorProps) {
  return (
    <components.DropdownIndicator {...props}>
      <ChevronDownIcon />
    </components.DropdownIndicator>
  );
}

function ClearIndicator(props: ClearIndicatorProps) {
  return (
    <components.ClearIndicator {...props}>
      <XIcon className="size-4" />
    </components.ClearIndicator>
  );
}

function MultiValueRemove(props: MultiValueRemoveProps) {
  return (
    <components.MultiValueRemove {...props}>
      <XIcon className="size-4" />
    </components.MultiValueRemove>
  );
}

const controlStyles = {
  base: "border rounded-lg bg-white hover:cursor-pointer text-sm dark:bg-slate-600 dark:border-slate-900",
  focus: "border-primary-600 ring-1 ring-primary-500 dark:ring-transparent",
  nonFocus: "border-gray-300 hover:border-gray-400",
  disabled: "bg-slate-100 text-slate-500",
};
const placeholderStyles = "text-gray-500 dark:text-gray-300 pl-1 py-0.5";
const selectInputStyles = "pl-1 py-0.5";
const valueContainerStyles = "p-1 gap-1";
const singleValueStyles = "leading-7 ml-1";
const multiValueStyles =
  "bg-gray-100 dark:bg-slate-800 dark:text-slate-200 rounded items-center py-0.5 pl-2 pr-1 gap-1.5";
const multiValueLabelStyles = "leading-6 py-0.5";
const multiValueRemoveStyles =
  "border border-gray-200 bg-white dark:bg-slate-600 dark:border-slate-950 dark:text-slate-300 hover:bg-red-50 hover:text-red-800 text-gray-500 hover:border-red-600/10 rounded-md";
const indicatorsContainerStyles = "p-1 gap-1";
const clearIndicatorStyles = "text-gray-500 p-1 rounded-md hover:bg-red-50 hover:text-red-800";
const indicatorSeparatorStyles = "bg-gray-300 dark:bg-slate-700";
const dropdownIndicatorStyles =
  "p-1 hover:bg-gray-100 dark:hover:bg-slate-900 dark:hover:text-slate-50 text-gray-500 dark:text-slate-300 rounded-md hover:text-black";
const menuStyles = "p-1 mt-2 border border-gray-200 bg-white dark:bg-slate-600 dark:border-slate-900 rounded-lg";
const groupHeadingStyles = "ml-3 mt-2 mb-1 text-gray-500 text-sm";
const optionStyles = {
  base: "hover:cursor-pointer px-3 py-2 rounded-lg text-sm dark:text-slate-300 dark:hover:text-slate-200",
  focus: "bg-gray-100 active:bg-gray-200 dark:bg-slate-900",
  selected: "after:content-['✔'] after:ml-2 after:text-green-500 text-gray-500 dark:text-slate-300",
};
const noOptionsMessageStyles = "text-gray-500 p-2 bg-gray-50 border border-dashed border-gray-200 rounded-sm";

export default function ReactSelect<
  Option,
  IsMulti extends boolean = true,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: Props<Option, IsMulti, Group>) {
  // https://react-select.com/typescript
  return (
    <Select
      isMulti={true as IsMulti}
      closeMenuOnSelect={true}
      hideSelectedOptions={false}
      unstyled={true}
      styles={{
        input: (base) => ({
          ...base,
          "input:focus": {
            boxShadow: "none",
          },
        }),
        // On mobile, the label will truncate automatically, so we want to
        // override that behaviour.
        multiValueLabel: (base) => ({
          ...base,
          whiteSpace: "normal",
          overflow: "visible",
        }),
        control: (base) => ({
          ...base,
          transition: "none",
        }),
      }}
      components={{
        // @ts-expect-error
        DropdownIndicator,
        // @ts-expect-error
        ClearIndicator,
        MultiValueRemove,
      }}
      classNames={{
        control: ({ isFocused, isDisabled }) => {
          return classNames(
            isFocused ? controlStyles.focus : controlStyles.nonFocus,
            controlStyles.base,
            isDisabled ? controlStyles.disabled : "",
          );
        },
        placeholder: () => placeholderStyles,
        input: () => selectInputStyles,
        valueContainer: () => valueContainerStyles,
        singleValue: () => singleValueStyles,
        multiValue: () => multiValueStyles,
        multiValueLabel: () => multiValueLabelStyles,
        multiValueRemove: () => multiValueRemoveStyles,
        indicatorsContainer: () => indicatorsContainerStyles,
        clearIndicator: () => clearIndicatorStyles,
        indicatorSeparator: () => indicatorSeparatorStyles,
        dropdownIndicator: () => dropdownIndicatorStyles,
        menu: () => menuStyles,
        groupHeading: () => groupHeadingStyles,
        option: ({ isFocused, isSelected }) => {
          return classNames(isFocused && optionStyles.focus, isSelected && optionStyles.selected, optionStyles.base);
        },
        noOptionsMessage: () => noOptionsMessageStyles,
      }}
      {...props}
    />
  );
}
